import { useClientPortalAPI } from "@certn/api";
import { components } from "@certn/api/generated-schemas/client-portal";
import { Text, Stack } from "@certn/cascadia";
import {
  BasicButton,
  ColumnDefinition,
  Container,
  DataTable,
  Layout,
  MediaQuery,
  Pagination,
  mq,
} from "@certn/cascadia-react";
import {
  orderingParamToSortDescriptor,
  sortDescriptorToOrderingParam,
} from "@certn/cascadia-react/components/data-tables/utils";
import { useMediaQuery } from "@certn/cascadia-react/hooks/useMediaQuery";
import { breakpoints } from "@certn/cascadia-tokens";
import { EVENT_NAMES, useEventTracking } from "@certn/utils";
import { formatDate } from "@certn/utils";
import { css } from "@emotion/css";
import { useMemo } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import { ActionsDropdown } from "./components/ActionsDropdown";
import { EmptyState } from "./components/EmptyState";
import { TableDataCard } from "./components/TableDataCard";
import { SortFilter } from "./components/filters/SortFilter";
import { StatusFilter } from "./components/filters/StatusFilter";
import { TagsFilter } from "./components/filters/TagsFilter";
import { normalizePackageOrdering, queryParamsToApiParams } from "./utils";
import { AppFooterLinks } from "../../components/app-footer/AppFooterLinks";
import { AppHeader } from "../../components/app-header/AppHeader";
import { AppNavigation } from "../../components/app-navigation/AppNavigation";
import { FilterBar } from "../../components/filters/FilterBar";
import { TagList } from "../../components/tag-list/TagList";
import { useQueryParams } from "../../hooks/useQueryParams";
import { useFlags } from "launchdarkly-react-client-sdk";

/**
 * Calculate the total number of checks in a package.
 * For international criminal record checks (CRIMINAL_RECORD_REPORT_1),
 * each country in the explicit_check_type array counts as a separate check.
 */
function calculateCheckCount(
  packageCheckSet: readonly components["schemas"]["PackageListPackagecheckSet"][]
): number {
  return packageCheckSet.reduce((total, check) => {
    // For international criminal record checks with explicit country selection
    if (
      check.check_type === "CRIMINAL_RECORD_REPORT_1" &&
      check.arguments &&
      typeof check.arguments === "object" &&
      "explicit_check_type" in check.arguments &&
      Array.isArray(check.arguments.explicit_check_type)
    ) {
      return total + check.arguments.explicit_check_type.length;
    }
    // For all other checks, count as 1
    return total + 1;
  }, 0);
}

export function PackageListView() {
  const { t } = useTranslation();
  const { trackEvent } = useEventTracking();
  const { enableClientAdjudication, packageImprovements } = useFlags();
  const styles = useStyles();
  const isMobile = useMediaQuery(
    `(max-width: ${breakpoints["breakpoint-sm"].maxWidth.toString()}px)`
  );
  const { params, setParam } = useQueryParams();
  const navigate = useNavigate();
  const location = useLocation();
  const ordering = normalizePackageOrdering(
    params.ordering,
    packageImprovements
  );
  const apiParams = useMemo(
    () =>
      queryParamsToApiParams(
        ordering !== undefined ? { ...params, ordering } : params
      ),
    [params, ordering]
  );
  const { data, mutate, isLoading } = useClientPortalAPI(
    `/api/internal/client-portal/packages/`,
    apiParams
  );
  const sortDescriptor =
    orderingParamToSortDescriptor(ordering ?? "") ??
    (packageImprovements
      ? { column: "modified", direction: "descending" as const }
      : undefined);

  const rows = data?.results;
  const noPackages = rows?.length === 0;
  const isFilteringActive = params.is_active?.includes("true");
  const isFilteringDeactivated = params.is_active?.includes("false");
  const noFiltersApplied = !isFilteringActive && !isFilteringDeactivated;

  // isFilteringActive and isFilteringDeactivated are exclusive (can't both be true, but both can be false),
  // you can simplify the check to just one condition for each, along with noFiltersApplied
  const isShowingEmptyState =
    noPackages &&
    (noFiltersApplied || isFilteringActive || isFilteringDeactivated);

  const getEmptyStateMessage = () => {
    if (!noPackages) return "";

    if (noFiltersApplied) {
      return t("You have no packages yet.");
    } else if (isFilteringDeactivated) {
      return t("You have no deactivated packages.");
    } else if (isFilteringActive) {
      return t("You have no active packages.");
    }
    return "";
  };

  const showDataTableOrEmptyScreens = () => {
    if (isShowingEmptyState) {
      return <EmptyState message={getEmptyStateMessage()} />;
    }

    return (
      <DataTable
        isLoading={isLoading}
        columns={columns}
        rows={rows || []}
        aria-label={t("Users Table")}
        testId="user-list-view-data-table"
        onRowAction={(id) => {
          const packageItem = rows?.find((row) => row.id === id);
          const viewOnly =
            packageImprovements && (packageItem?.case_count ?? 0) > 0;
          navigate(viewOnly ? `/package/${id}?view=true` : `/package/${id}`, {
            state: {
              last: { href: `${location.pathname}${location.search}` },
            },
          });
        }}
        onSortChange={(nextSortDescriptor) =>
          setParam(
            "ordering",
            sortDescriptorToOrderingParam(nextSortDescriptor)
          )
        }
        sortDescriptor={sortDescriptor}
      />
    );
  };

  const columns: ColumnDefinition<components["schemas"]["PackageList"]>[] = [
    {
      key: "name",
      name: t("Name"),
      isRowHeader: true,
      allowsSorting: true,
      width: "25%",
      testID: "package-name-column-header",
      render({ name, is_active }: { name: string; is_active: boolean }) {
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <Text testID="package-list-name" style="global/body-secondary">
              {name || "--"}
            </Text>
            {!is_active && (
              <Text testID="package-active" style="global/info-label">
                {t("(Deactivated)")}
              </Text>
            )}
          </div>
        );
      },
    },
    {
      key: "checks-count",
      name: t("Checks"),
      isRowHeader: true,
      width: "10%",
      testID: "check-number-column-header",
      render({
        packagecheck_set,
      }: {
        packagecheck_set: readonly components["schemas"]["PackageListPackagecheckSet"][];
      }) {
        const checkCount = calculateCheckCount(packagecheck_set);
        return (
          <div style={{ gap: 10 }}>
            <Text testID="checks-in-package" style="form/body-secondary">
              {packageImprovements
                ? checkCount
                : `${checkCount} ${t("checks")}`}
            </Text>
          </div>
        );
      },
    },
    {
      key: packageImprovements ? "case_count" : "usage",
      name: t("Usage"),
      isRowHeader: true,
      allowsSorting: true,
      width: "10%",
      testID: "package-usage-column-header",
      render({ case_count }: { case_count: number }) {
        return (
          <div style={{ gap: 10 }}>
            <Text testID="package-case-count" style="form/body-secondary">
              {packageImprovements
                ? t("{{count}} case", { count: case_count })
                : `${case_count} ${t("cases")}`}
            </Text>
          </div>
        );
      },
    },
    {
      key: "tags",
      name: t("Tags"),
      isRowHeader: true,
      width: "15%",
      testID: "package-tags-column-header",
      render({
        tags,
      }: {
        tags: readonly components["schemas"]["PackageListTags"][];
      }) {
        return (
          <TagList
            tags={tags.map((tag) => tag.name)}
            capacity={2}
            hasOverflowDetails
            testID="package"
          />
        );
      },
    },
    enableClientAdjudication && {
      key: "adjudication",
      name: t("Adjudication"),
      isRowHeader: true,
      width: "25%",
      testID: "package-adjudication-column-header",
      render({
        adjudication_rubric,
      }: {
        adjudication_rubric: components["schemas"]["PackageListAdjudicationRubric"];
      }) {
        return (
          <Text testID="package-adjudication" style="global/body-secondary">
            {adjudication_rubric?.name}
          </Text>
        );
      },
    },
    {
      key: packageImprovements ? "modified" : "created",
      name: packageImprovements ? t("Date Modified") : t("Created date"),
      isRowHeader: true,
      allowsSorting: true,
      width: "15%",
      testID: packageImprovements
        ? "package-modified-column-header"
        : "package-created-column-header",
      render({ created, modified }: { created: string; modified: string }) {
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <Text
              testID={
                packageImprovements
                  ? "package-modified-date"
                  : "package-created-date"
              }
              style="form/body-secondary"
            >
              {formatDate(packageImprovements ? modified : created)}
            </Text>
          </div>
        );
      },
    },
    {
      key: "actions",
      isAction: true,
      width: "5%",
      render({
        id,
        is_active,
        name,
        case_count,
      }: {
        id: string;
        is_active: boolean;
        name: string;
        case_count: number;
      }) {
        return (
          <ActionsDropdown
            id={id}
            packageName={name}
            isActive={is_active}
            caseCount={case_count}
            refreshPackage={mutate}
          />
        );
      },
    },
  ].filter(Boolean);

  return (
    <Layout
      layout="narrow"
      header={<AppHeader />}
      navigation={<AppNavigation />}
      footer={<AppFooterLinks />}
    >
      <Container layout="full-width">
        <div className={styles.pageTitleSection}>
          <Text
            as="h1"
            style={isMobile ? "global/title-small" : "global/title-medium"}
          >
            {t("Package management")}
          </Text>
          <BasicButton
            testID="new-package-button"
            onPress={() => {
              trackEvent(EVENT_NAMES.CP_NEW_PACKAGE, { orderType: "new" });
              navigate(`/packages/create/`);
            }}
          >
            {t("New package")}
          </BasicButton>
        </div>
        <MediaQuery sm type="hide">
          <Stack orientation="horizontal" gap="space-2">
            <Text color="text-weak">
              <Trans>
                A complete list of all packages that have been created for your
                organization. Packages allow you to create customizable sets of
                background checks tailored to the unique needs of your
                organization or department.
              </Trans>
            </Text>
          </Stack>
        </MediaQuery>
        <FilterBar hasSearch testID="packages">
          <MediaQuery sm type="show">
            <SortFilter />
          </MediaQuery>
          <TagsFilter />
          <StatusFilter />
        </FilterBar>
        <MediaQuery sm type="hide">
          {showDataTableOrEmptyScreens()}
        </MediaQuery>
        <MediaQuery sm type="show">
          <Container layout="full-width">
            {rows &&
              rows.map((packageItem) => {
                return (
                  <TableDataCard
                    name={packageItem.name}
                    id={packageItem.id}
                    key={packageItem.id}
                    caseCount={packageItem.case_count}
                    checkCount={calculateCheckCount(
                      packageItem.packagecheck_set
                    )}
                    createdDate={packageItem.created}
                    modifiedDate={packageItem.modified}
                    isActive={packageItem.is_active}
                    refreshPackage={mutate}
                  />
                );
              })}
          </Container>
        </MediaQuery>
        {!noPackages && (
          <Pagination
            setPage={(page) => setParam("page", page.toString())}
            setPageCount={(pageCount) =>
              setParam("page_size", pageCount.toString())
            }
            total={data?.count ?? 0}
            page={params.page ? Number(params.page) : 1}
            pageCount={params["page_size"] ? Number(params["page_size"]) : 25}
          />
        )}
      </Container>
    </Layout>
  );
}

function useStyles() {
  return {
    pageTitleSection: css(
      {
        display: "flex",
        justifyContent: "space-between",
        gap: "var(--space-2)",
      },
      mq({
        flexDirection: ["column", "row"],
      })
    ),
  };
}
